import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"
import {
  BulkAiCallProgressDto,
  BulkAiCallCandidateProgressDto,
  BulkAiCallCandidateStatus,
} from "@/modules/recruitment/applications/applicants-service"

export interface BulkAiCallCandidate {
  applicationId: string
  candidateName: string
  status: BulkAiCallCandidateStatus
  failureReason?: string | null
}

export interface BulkAiCallJob {
  jobId: string
  total: number
  processed: number
  failed: number
  status: "Pending" | "InProgress" | "Completed" | "Failed"
  candidates: BulkAiCallCandidate[]
}

interface BulkAiCallContextValue {
  jobs: BulkAiCallJob[]
  /** Count of jobs that are still Pending or InProgress */
  activeCount: number
  addJob: (job: BulkAiCallJob) => void
  updateJob: (progress: BulkAiCallProgressDto) => void
  updateCandidate: (progress: BulkAiCallCandidateProgressDto) => void
  clearCompleted: () => void
}

const BulkAiCallContext = createContext<BulkAiCallContextValue | null>(null)

export function BulkAiCallProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<BulkAiCallJob[]>([])

  const addJob = useCallback((job: BulkAiCallJob) => {
    setJobs((prev) => [...prev, job])
  }, [])

  const updateJob = useCallback((progress: BulkAiCallProgressDto) => {
    setJobs((prev) => {
      const exists = prev.some((j) => j.jobId === progress.jobId)
      if (!exists) {
        return [...prev, { ...progress, candidates: progress.candidates ?? [] }]
      }
      return prev.map((j) =>
        j.jobId === progress.jobId ? { ...j, ...progress } : j,
      )
    })
  }, [])

  const updateCandidate = useCallback(
    (progress: BulkAiCallCandidateProgressDto) => {
      setJobs((prev) => {
        const jobExists = prev.some((j) => j.jobId === progress.jobId)
        if (!jobExists) {
          const placeholder: BulkAiCallJob = {
            jobId: progress.jobId,
            total: 0,
            processed: 0,
            failed: 0,
            status: "InProgress",
            candidates: [
              {
                applicationId: progress.applicationId,
                candidateName: progress.candidateName,
                status: progress.status,
                failureReason: progress.failureReason,
              },
            ],
          }
          return [...prev, placeholder]
        }

        return prev.map((j) => {
          if (j.jobId !== progress.jobId) return j

          const candidateExists = j.candidates.some(
            (c) => c.applicationId === progress.applicationId,
          )
          const updatedCandidate: BulkAiCallCandidate = {
            applicationId: progress.applicationId,
            candidateName: progress.candidateName,
            status: progress.status,
            failureReason: progress.failureReason,
          }

          return {
            ...j,
            candidates: candidateExists
              ? j.candidates.map((c) =>
                  c.applicationId === progress.applicationId
                    ? updatedCandidate
                    : c,
                )
              : [...j.candidates, updatedCandidate],
          }
        })
      })
    },
    [],
  )

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((j) => j.status !== "Completed" && j.status !== "Failed"),
    )
  }, [])

  const activeCount = jobs.filter(
    (j) => j.status === "Pending" || j.status === "InProgress",
  ).length

  return (
    <BulkAiCallContext.Provider
      value={{
        jobs,
        activeCount,
        addJob,
        updateJob,
        updateCandidate,
        clearCompleted,
      }}
    >
      {children}
    </BulkAiCallContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBulkAiCallContext(): BulkAiCallContextValue {
  const ctx = useContext(BulkAiCallContext)
  if (!ctx)
    throw new Error(
      "useBulkAiCallContext must be used inside <BulkAiCallProvider>",
    )
  return ctx
}
